import { MonetizationType, PresentationType } from '@/@types/graphql-types';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { BuyboxRegularOffer } from '@/features/buybox/types/types';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useExperiment } from '@/libraries/experiment';
import {
	ExperimentAffiliationPlacement,
	getExperimentAffiliateTag,
} from '@/libraries/experiment/experiment-affiliation';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

export function useBuyRentExp({
	title,
	offer,
}: {
	title: MaybeRefOrGetter<TitleDetail>;
	offer?: MaybeRefOrGetter<BuyboxRegularOffer>;
}) {
	const { country } = storeToRefs(useLanguageStore());
	const { activeVariantWithControlGroup: variant, experimentInfo } = useExperiment('EXP_MERGE_BUY_RENT');

	const _title = computed(() => toValue(title));
	const _offer = computed(() => toValue(offer));

	// Create a map of providers with both buy and rent offers
	const buyRentOffersByProvider = computed(() => {
		if (!variant.value || variant.value === 'control') return {};

		const map: Record<
			string,
			{
				offers: Record<MonetizationType.Buy | MonetizationType.Rent, BuyboxRegularOffer>;
				retailPrices: Record<MonetizationType.Buy | MonetizationType.Rent, string>;
			}
		> = {};

		const buyOffers = _title.value.buy.filter(
			offer =>
				![PresentationType.Bluray, PresentationType.Bluray_4K, PresentationType.Dvd].includes(
					offer.presentationType
				) && offer.country === country.value
		);
		const rentOffers = _title.value.rent.filter(offer => offer.country === country.value);

		buyOffers.forEach(buyOffer => {
			const rentOffer = rentOffers.find(r => r.package.shortName === buyOffer.package.shortName);

			if (buyOffer.retailPrice && rentOffer?.retailPrice) {
				map[buyOffer.package.shortName] = {
					offers: {
						[MonetizationType.Buy]: buyOffer,
						[MonetizationType.Rent]: rentOffer,
					},
					retailPrices: {
						[MonetizationType.Buy]: buyOffer.retailPrice,
						[MonetizationType.Rent]: rentOffer.retailPrice,
					},
				};
			}
		});

		return map;
	});

	const buyRentOffers = computed<Record<MonetizationType.Buy | MonetizationType.Rent, BuyboxRegularOffer> | null>(
		() => {
			if (!_offer.value || !variant.value) return null;
			if (![MonetizationType.Buy, MonetizationType.Rent].includes(_offer.value.monetizationType)) return null;
			return buyRentOffersByProvider.value[_offer.value.package.shortName]?.offers ?? null;
		}
	);

	const buyRentOfferRetailPrices = computed<Record<MonetizationType.Buy | MonetizationType.Rent, string> | null>(
		() => {
			if (!_offer.value || !variant.value) return null;
			if (![MonetizationType.Buy, MonetizationType.Rent].includes(_offer.value.monetizationType)) return null;
			return buyRentOffersByProvider.value[_offer.value.package.shortName]?.retailPrices ?? null;
		}
	);

	const trackingEventLabel = computed(() => {
		if (!_offer.value || !variant.value) return null;
		if (
			![MonetizationType.Buy, MonetizationType.Rent].includes(_offer.value.monetizationType) ||
			[PresentationType.Bluray, PresentationType.Bluray_4K, PresentationType.Dvd].includes(
				_offer.value.presentationType
			)
		)
			return null;

		if (buyRentOfferRetailPrices.value) return 'buyrent';
		return _offer.value.monetizationType === MonetizationType.Buy ? 'buy' : 'rent';
	});

	function filterBuyOffersBuyRentExp(offer: TitleOfferFragment) {
		if (!variant.value || variant.value === 'control') return false;
		return (
			!!buyRentOffersByProvider.value[offer.package.shortName] && offer.monetizationType === MonetizationType.Buy
		);
	}

	function setBuyRentExpTags({
		url,
		type,
		monetizationType,
		shortName,
	}: {
		url: ClickoutUrl;
		type: 'ro' | 'ft';
		monetizationType: MonetizationType;
		shortName: string;
	}) {
		if (!variant.value) return url;
		if (!['atp', 'amp', 'prv', 'hlu', 'cru', 'itu', 'amz'].includes(shortName)) return url;

		const affiliationShortId = experimentInfo.value.affiliationShortId as string;

		let placement = type as ExperimentAffiliationPlacement;

		if (buyRentOfferRetailPrices.value) {
			placement = 'buyrent';
		} else if ([MonetizationType.Buy, MonetizationType.Rent].includes(monetizationType) && type === 'ro') {
			placement = monetizationType === MonetizationType.Buy ? 'buy' : 'rent';
		}

		const tag = getExperimentAffiliateTag({
			affiliationShortId,
			variant: variant.value,
			country: country.value,
			placement,
			providerShortName: shortName,
		});

		return url.set('uct_ctag', tag);
	}

	return {
		buyRentOffers,
		buyRentOfferRetailPrices,
		filterBuyOffersBuyRentExp,
		setBuyRentExpTags,
		trackingEventLabel,
	};
}
